import { useState } from "react";
import { Sparkle, ArrowRight, X, Calendar, ChartBar } from "@phosphor-icons/react";

/**
 * WelcomePlanHero
 * One-time hero card shown on the Dashboard right after onboarding when the
 * AI starter plan finished generating. Reads the small payload that
 * Onboarding stashes in `localStorage["uptop-welcome-plan"]` and renders an
 * inviting card with two CTAs: "Open your plan" → Plans tab, or dismiss.
 *
 * Once dismissed (or "Open plan" tapped) the localStorage key is removed so
 * the card never re-appears on subsequent visits.
 */
export default function WelcomePlanHero({ onNavigate }) {
  const [payload, setPayload] = useState(() => {
    try {
      const raw = localStorage.getItem("uptop-welcome-plan");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });

  if (!payload || !payload.name) return null;

  const dismiss = () => {
    try { localStorage.removeItem("uptop-welcome-plan"); } catch (e) { /* ignore */ }
    setPayload(null);
  };

  const open = () => {
    try { localStorage.removeItem("uptop-welcome-plan"); } catch (e) { /* ignore */ }
    setPayload(null);
    onNavigate?.("plans");
  };

  return (
    <div
      className="relative bg-gradient-to-br from-[#E05A1D]/15 via-[#E05A1D]/5 to-transparent border border-[#E05A1D]/40 p-5 ai-glow overflow-hidden"
      data-testid="welcome-plan-hero"
    >
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-[#71717A] hover:text-white transition-colors"
        data-testid="welcome-plan-dismiss"
        aria-label="Dismiss"
      >
        <X size={16} weight="bold" />
      </button>

      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 flex items-center justify-center border border-[#E05A1D]/50 bg-[#E05A1D]/15 text-[#E05A1D] shrink-0">
          <Sparkle size={20} weight="fill" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E05A1D] mb-1">
            New · Your AI Plan
          </p>
          <h3
            className="text-xl sm:text-2xl font-bold tracking-tight uppercase text-white leading-tight truncate"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            data-testid="welcome-plan-name"
          >
            {payload.name}
          </h3>
          {(payload.weeks_count || payload.days_per_week) && (
            <div className="flex items-center gap-4 mt-2">
              {payload.weeks_count > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-[#A1A1AA]">
                  <Calendar size={12} weight="bold" />
                  <span><strong className="text-white">{payload.weeks_count}</strong> weeks</span>
                </div>
              )}
              {payload.days_per_week > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-[#A1A1AA]">
                  <ChartBar size={12} weight="bold" />
                  <span><strong className="text-white">{payload.days_per_week}</strong> days / week</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <p className="text-sm text-[#A1A1AA] leading-relaxed mb-4">
        We've built you a starter plan based on your goal. Open it, tweak it, or apply
        it straight to your calendar.
      </p>

      <div className="flex gap-2">
        <button
          onClick={open}
          className="flex-1 bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-2.5 text-xs flex items-center justify-center gap-2 transition-colors active:scale-[0.98]"
          data-testid="welcome-plan-open-btn"
        >
          Open your plan
          <ArrowRight size={14} weight="bold" />
        </button>
        <button
          onClick={dismiss}
          className="border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#3f3f46] font-bold uppercase tracking-wide py-2.5 px-4 text-xs transition-colors"
          data-testid="welcome-plan-later-btn"
        >
          Later
        </button>
      </div>
    </div>
  );
}
